import './UpgradeForm.scss';
import {
  NinjaSubscriptionPlan,
  CanonicalInterval,
  PlanSectionType,
  SubscriptionPlanInfo,
} from 'src/types';
import { SVG_SIZE_M } from 'src/constants';
import { CheckCircle } from '@phosphor-icons/react';
import { useCallback, useMemo } from 'react';
import { useSession } from 'src/hooks';
import { Button } from 'src/v2/commonComponents/Button';
import classNames from 'classnames';
import { CommonPlanCardContent } from '../CommonPlanCardContent';
import { getReferralId } from 'src/utils';

type UpgradeFormProps = {
  onOpenCheckout?: (type: PlanSectionType, period: CanonicalInterval) => void;
  // handleBuyWithAws?: (type: PlanSectionType, period: CanonicalInterval) => void;
  period: CanonicalInterval;
  type: PlanSectionType;
  isSelected: boolean;
  selectedPlan?: SubscriptionPlanInfo;
  isTeams?: boolean;
};

/**
 * @deprecated
 */
export const UpgradeForm = ({
  onOpenCheckout,
  // handleBuyWithAws,
  period,
  type,
  isSelected,
  selectedPlan,
  isTeams,
}: UpgradeFormProps) => {
  const {
    isProTier,
    isUltraTier,
    isStandardTier,
    isSubscribedTier,
    isTeamsStandardTier,
    isTeamsProTier,
    isTeamsUltraTier,
    isTeamsTier,
  } = useSession();

  const referralId = getReferralId();

  const isMonthlyPeriod = period === CanonicalInterval.MONTHLY;

  const isProType = type === 'Pro' || type === 'Teams_Pro';
  const isStandardType = type === 'Standard' || type === 'Teams_Standard';
  const isUltraType = type === 'Ultra' || type === 'Teams_Ultra';

  const roundAmount = useCallback((value?: number) => {
    if (value !== 0 && !value) {
      return value;
    }
    return Math.round(value / 100);
  }, []);

  const originalPriceLabel = useMemo(() => {
    const price = isTeams
      ? selectedPlan?.per_seat?.original_price
      : selectedPlan?.original_price;
    if (!price) {
      return '-';
    }
    return roundAmount(isMonthlyPeriod ? price : price / 12);
  }, [
    selectedPlan?.original_price,
    selectedPlan?.per_seat,
    isMonthlyPeriod,
    roundAmount,
    isTeams,
  ]);

  const discountedPriceLabel = useMemo(() => {
    const price = isTeams
      ? selectedPlan?.per_seat?.total_recurring_amount
      : selectedPlan?.total_recurring_amount;
    if (!price) {
      return '-';
    }
    return roundAmount(isMonthlyPeriod ? price : price / 12);
  }, [
    selectedPlan?.total_recurring_amount,
    selectedPlan?.per_seat,
    isMonthlyPeriod,
    roundAmount,
    isTeams,
  ]);

  const isDowngrade = useMemo(
    () =>
      ((isUltraTier && type === 'Pro') || type === 'Standard') &&
      isSubscribedTier,
    [type, isUltraTier, isSubscribedTier],
  );

  const isCurrentPlan = useMemo(() => {
    return (
      (isStandardTier && type === 'Standard') ||
      (isProTier && type === 'Pro') ||
      (isUltraTier && type === 'Ultra') ||
      (isTeamsStandardTier && type === 'Teams_Standard') ||
      (isTeamsProTier && type === 'Teams_Pro') ||
      (isTeamsUltraTier && type === 'Teams_Ultra')
    );
  }, [
    isStandardTier,
    isProTier,
    isUltraTier,
    type,
    isTeamsStandardTier,
    isTeamsProTier,
    isTeamsUltraTier,
  ]);

  const buttonLabel = useMemo(() => {
    if (isCurrentPlan) {
      return 'Current plan';
    }

    if (type === 'Standard' && isSubscribedTier) {
      return 'Downgrade to Standard';
    }

    if (type === 'Teams_Standard' && isTeamsTier) {
      return 'Downgrade to Business Standard';
    }

    if (type === 'Ultra' && isSubscribedTier) {
      return 'Upgrade to Ultra';
    }

    if (type === 'Teams_Ultra' && isTeamsTier) {
      return 'Upgrade to Business Ultra';
    }

    if (type === 'Pro' && isUltraTier) {
      return 'Downgrade to Pro';
    }

    if (type === 'Teams_Pro' && isTeamsUltraTier) {
      return 'Downgrade to Business Pro';
    }

    if (type === 'Pro' && isStandardTier) {
      return 'Upgrade to Pro';
    }

    if (type === 'Teams_Pro' && isTeamsStandardTier) {
      return 'Upgrade to Business Pro';
    }

    return isTeams ? 'Buy Business' : `Get ${type}`;
  }, [
    isStandardTier,
    isUltraTier,
    isSubscribedTier,
    type,
    isCurrentPlan,
    isTeams,
    isTeamsStandardTier,
    isTeamsUltraTier,
    isTeamsTier,
  ]);

  const onBuyWithAwsClick = () => {
    // handleBuyWithAws?.(type, period);
  };
  const onCheckoutClick = () => {
    onOpenCheckout?.(type, period);
  };

  const showBuyWithAws = isTeams && !isTeamsTier;

  return (
    <div className={classNames('nj-upgrade-form')}>
      <div
        className={classNames({
          'nj-upgrade-form--team-header': isTeams,
          'nj-upgrade-form--individual-header': !isTeams,
        })}
      >
        <div className="nj-plan-section--current-plan">
          <div className="nj-plan-section--header-wrapper">
            <h5 className="nj-plan-section--title-wrapper">
              <span className="nj-plan-section--title">
                {isTeams ? 'Business ' : ''}
                {!isTeams && type}
              </span>

              {isMonthlyPeriod &&
                !!selectedPlan?.one_time_discount_percentage &&
                !referralId && (
                  <p className="nj-plan-section--current-plan-sales-description">
                    {`${selectedPlan?.one_time_discount_percentage}% off first month`}
                  </p>
                )}

              {period === CanonicalInterval.ANNUALLY && !referralId && (
                <p className="nj-plan-section--current-plan-sales-description">
                  Billed annually
                </p>
              )}

              {!!referralId &&
                selectedPlan?.plan_tier === NinjaSubscriptionPlan.ULTRA && (
                  <div className="nj-plan-section--most-popular-label">
                    Most popular
                  </div>
                )}
            </h5>

            <p className="nj-plan-section--current-plan-price">
              {selectedPlan?.discounted_price !== 0 &&
                discountedPriceLabel !== originalPriceLabel && (
                  <span className="nj-plan-section--current-plan-original-price">
                    <s>${originalPriceLabel}</s>
                    &nbsp;
                  </span>
                )}
              <span className="nj-plan-section--current-plan-title">
                $
                {selectedPlan?.discounted_price !== 0
                  ? discountedPriceLabel
                  : originalPriceLabel}
                &nbsp;
              </span>
              <span className="nj-plan-section--current-plan-subtitle">
                {isTeams ? 'per seat/month' : '/mo'}
              </span>
            </p>

            <span className="nj-plan-section--current-plan-description colored">
              Unlimited tasks
            </span>
          </div>
        </div>

        <div className="nj-upgrade-form--button-wrapper">
          {isSelected ? (
            <button
              type="button"
              className="nj-upgrade-form--selected"
              disabled
            >
              <CheckCircle size={SVG_SIZE_M} weight="fill" />
              <span>Selected</span>
            </button>
          ) : (
            <button
              type="button"
              className={`nj-button nj-upgrade-form--submit ${isDowngrade ? 'nj-downgrade-button' : ''}`}
              onClick={onCheckoutClick}
              id="Plan-card-upgrade"
              disabled={isCurrentPlan}
              data-e2e={`upgrade-to-${type}-button`}
            >
              {buttonLabel}
            </button>
          )}
          {showBuyWithAws && (
            <Button
              type="button"
              fullWidth
              className={'nj-upgrade-form--buy-with-aws'}
              onClick={onBuyWithAwsClick}
              id="Plan-card-buy-with-aws"
              disabled={isCurrentPlan}
              data-e2e={`buy-with-aws-${type}-button`}
            >
              Buy with AWS
            </Button>
          )}

          <p className="nj-upgrade-form--caption">
            Cancel anytime, effective next billing cycle
          </p>
        </div>
      </div>

      <CommonPlanCardContent
        isTeams={isTeams}
        isStandardType={isStandardType}
        isProType={isProType}
        isUltraType={isUltraType}
      />
    </div>
  );
};
